import React from "react";

const responseTypeLabel = (value) => String(value)
  .replace(/([a-z])([A-Z])/g, "$1 $2")
  .replace(/_/g, " ");

const requirementLabel = (question) => {
  if (question.required) return "Required";
  if (question.conditionalRequired) return "Conditionally required";
  return "Optional";
};

export default function CampaignReadOnlyQuestionnairePreview({ preview }) {
  const questionnaire = preview.questionnaire;
  const persistedDraft = preview.schemaVersion
    === "campaign_uat_public_questionnaire_preview.v3";
  const responseRehearsal = preview.schemaVersion
    === "campaign_uat_public_questionnaire_preview.v4";
  const multiAnswerRehearsal = preview.schemaVersion
    === "campaign_uat_public_questionnaire_preview.v5";

  return (
    <section
      aria-labelledby="campaign-questionnaire-preview-title"
      className="mt-5 rounded-xl border border-violet-200 bg-white p-5"
      data-testid="campaign-read-only-questionnaire-preview"
    >
      <div className="rounded-lg border border-violet-200 bg-violet-50 p-3 text-sm text-violet-900">
        <h2
          id="campaign-questionnaire-preview-title"
          className="text-lg font-semibold"
        >
          Questionnaire preview - read only
        </h2>
        <p className="mt-1">
          {multiAnswerRehearsal
            ? "Review the immutable questions prepared for this Campaign. A separate isolated rehearsal can partially save, review, and explicitly submit typed synthetic answers; production submission, uploads, booking, and CRM changes are disabled."
            : responseRehearsal
            ? "Review the immutable question prepared for this Campaign. A separate isolated rehearsal can save, review, and explicitly submit one synthetic response; production submission, uploads, booking, and CRM changes are disabled."
            : persistedDraft
              ? "Review the immutable question prepared for this Campaign. A separate synthetic rehearsal can save one session-bound text draft; submission, uploads, booking, and CRM changes are disabled."
            : "Review the questionnaire prepared for this Campaign. Authoritative answer entry, uploads, saving, submission, booking, and CRM changes are disabled."}
        </p>
      </div>

      <div
        className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900"
        role="status"
      >
        {multiAnswerRehearsal
          ? "Safety verified: the typed response is bound to this exact Testerson 2 answer set and available only to the verified recipient or owning advisor; providers, email, legacy submission writes, engagement activity, execution, queueing, and delivery remain disabled."
          : responseRehearsal
          ? "Safety verified: the response is bound to this exact Testerson 2 rehearsal lineage and available only to the verified recipient or owning advisor; providers, email, legacy submission writes, engagement activity, execution, queueing, and delivery remain disabled."
          : persistedDraft
            ? "Safety verified: draft persistence is isolated to this verified Testerson 2 session; submission, providers, email, execution, queueing, and delivery remain disabled."
          : "Safety verified: this preview cannot persist or transmit answers; providers, email, execution, queueing, and delivery remain disabled."}
      </div>

      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Questionnaire
          </dt>
          <dd className="mt-1 text-sm font-medium text-slate-900">
            {questionnaire.name}
          </dd>
        </div>
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Published version
          </dt>
          <dd className="mt-1 text-sm font-medium text-slate-900">
            {questionnaire.version}
          </dd>
        </div>
      </dl>

      <ol className="mt-4 space-y-4" aria-label="Questionnaire sections">
        {questionnaire.sections.map((section) => {
          const sectionTitleId = `campaign-preview-section-${section.sequence}`;
          return (
            <li key={section.sequence}>
              <section
                aria-labelledby={sectionTitleId}
                className="rounded-xl border border-slate-200 p-4"
              >
                <h3
                  id={sectionTitleId}
                  className="text-base font-semibold text-slate-900"
                >
                  Section {section.sequence}: {section.title}
                </h3>
                {section.description ? (
                  <p className="mt-1 text-sm text-slate-600">
                    {section.description}
                  </p>
                ) : null}
                <ol
                  className="mt-3 space-y-3"
                  aria-label={`Questions in ${section.title}`}
                >
                  {section.questions.map((question) => {
                    const questionTitleId = `${sectionTitleId}-question-${question.sequence}`;
                    return (
                      <li key={question.sequence}>
                        <article
                          aria-labelledby={questionTitleId}
                          className="rounded-lg border border-slate-200 bg-slate-50 p-3"
                        >
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <h4
                              id={questionTitleId}
                              className="text-sm font-medium text-slate-900"
                            >
                              {question.sequence}. {question.label}
                            </h4>
                            <span className="rounded-full bg-slate-200 px-2 py-1 text-xs font-medium text-slate-700">
                              {requirementLabel(question)}
                            </span>
                          </div>
                          <p className="mt-1 text-xs capitalize text-slate-500">
                            Response type: {responseTypeLabel(question.responseType)}
                          </p>
                          {question.conditionalVisibility ? (
                            <p className="mt-1 text-xs text-slate-500">
                              Shown conditionally
                            </p>
                          ) : null}
                          {question.helpText ? (
                            <p className="mt-2 text-sm text-slate-600">
                              {question.helpText}
                            </p>
                          ) : null}
                          {question.options.length ? (
                            <ul
                              className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-700"
                              aria-label={`Choices for ${question.label}`}
                            >
                              {question.options.map((option) => (
                                <li key={option.sequence}>{option.label}</li>
                              ))}
                            </ul>
                          ) : null}
                        </article>
                      </li>
                    );
                  })}
                </ol>
              </section>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
